import { styled } from "@mui/material/styles";
import { Actions } from "csi/def/Actions";
import DojoView, { DojoViewProps } from "csi/page/DojoView";
import { useStore } from "csi/state/hooks";
import SaveDialog, {
	SaveDialogProps,
} from "designSystem/components/dialog/SaveDialog";
import SaveDialogStore from "designSystem/components/dialog/store/SaveDialogStore";
import { useSyncLocalStore } from "designSystem/hooks";
import {
	Box,
	FlexColumn,
	ModalDialogDrawerProps,
	Tab,
	TabPanel,
	Tabs,
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { blue } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useMemo } from "react";
import { GoToDialogButtons } from "./GoToDialogButtons";
import GoToDialogStore, {
	GoToDialogStoreContext,
	useGoToDialogStore,
} from "./GoToDialogStore";
import SelectionOverviewPane, {
	SelectionOverviewPaneClosed,
} from "./SelectionOverviewPane";
import SuggestedChip from "./SuggestedChip";

const PREFIX = "GoToDialog";

const classes = {
	buttonClosed: `${PREFIX}buttonClosed`,
};

const StyledSaveDialog = styled(SaveDialog)(() => ({
	// styling for SelectionOverviewPane drawer
	[`& .${classes.buttonClosed}`]: {
		backgroundColor: blue[50],
		borderColor: blue[600],
		left: "unset",
		borderRadius: 12,
		paddingRight: 12,
		width: "auto",
		"&:hover": {
			backgroundColor: blue[100],
		},
		"& .csi-drawer-button-label": {
			color: blue[600],
			fontSize: 12,
			fontWeight: "bold",
			whiteSpace: "nowrap",
		},
		"& .MuiSvgIcon-root": {
			color: blue[600],
		},
	},
}));

export interface GoToDialogProps {
  /** legacy dojo actions */
  legacyActions?: Actions;

  /** dialog properties */
  saveDialogProps: SaveDialogProps;

  /** store */
  store: GoToDialogStore;

  /** z-index of dialog */
  zIndex?: number;
}

interface GoToDialogTabProps {
  /** tab index */
  index: number;
}

const GoToDialogView: FunctionComponent<DojoViewProps> = (props) => {
	const { onViewLoad } = useGoToDialogStore();
	const { viewParams, ...otherProps } = props;

	return (
		<DojoView
			forceReadOnly={!viewParams.defView.m_formData?.m_bEditable}
			fullHeight
			onViewLoad={onViewLoad}
			viewId={viewParams.defView.m_id}
			viewParams={{
				bPopup: true,
				flexHeight: true,
				inReactDialog: true,
				...viewParams,
			}}
			{...otherProps}
		/>
	);
};

const GoToDialogTab: FunctionComponent<GoToDialogTabProps> = observer((props) => {
	const { getViewProps, hasViewedTab, tabIndex } = useGoToDialogStore();
	const { index } = props;

	return (
		<TabPanel
			flexGrow={1}
			index={index}
			selectedTabIndex={tabIndex}
		>
			{hasViewedTab(index) &&
				<GoToDialogView {...getViewProps(index)} />
			}
		</TabPanel>
	);
},
);

const GoToDialogTabs = observer(() => {
	const { getTabLabel, isTabSuggested, onChangeTab, tabIndex, tabs } = useGoToDialogStore();
	const loc = useLocale();

	return (
		<FlexColumn width={1}>
			<Tabs onChange={onChangeTab} value={tabIndex}>
				{tabs.map((tab, i) => (
					<Tab
						icon={isTabSuggested(tab) ? <SuggestedChip /> : null}
						iconPosition="end"
						key={i}
						label={getTabLabel(tab, loc)}
					/>
				))}
			</Tabs>
			<Box growNoOverflow mt={3}>
				{tabs.map((tab, i) => (
					<GoToDialogTab index={i} key={i} />
				))}
			</Box>
		</FlexColumn>
	);
});

const GoToDialog: FunctionComponent<GoToDialogProps> = observer((props) => {
	const {
		legacyActions,
		store,
		saveDialogProps,
		zIndex,
	} = props;
	const {
		allowSave,
		getViewProps,
		hasTabs,
		isFirstViewLoaded,
		numberOfSelections,
		onToggleOpen,
		onTransition,
		selectedTab: { view },
		useDrawer,
	} = store;
	const { localStore } = useStore();
	const loc = useLocale();
	const actions = legacyActions?.m_actions;
	const disableSave = !allowSave;

	// memoize the object here to ensure the reference to the object is the same even when re-rendered
	const rightDrawerProps: ModalDialogDrawerProps = useMemo(() => ({
		buttonAlwaysVisible: true,
		drawerNode: <SelectionOverviewPane />,
		drawerNodeWhenClosed: <SelectionOverviewPaneClosed />,
		onToggleOpen,
		onTransition,
		open: true,
		widthOpen: 280,
	}), []);

	// create a save dialog store to be used in SaveDialog
	const saveDialogStore = useSyncLocalStore(() => new SaveDialogStore());

	Object.assign(rightDrawerProps, {
		open: localStore.gotoDialogDrawerOpen,
		openButtonClassName: numberOfSelections ? classes.buttonClosed : "",
		openButtonLabel: numberOfSelections ? loc.resource("DialogSelectionCount", [numberOfSelections]) : "",
	});

	return (
		<GoToDialogStoreContext.Provider value={store}>
			<StyledSaveDialog
				customButtons={actions?.length && (
					<GoToDialogButtons
						actions={actions}
						disableSave={disableSave}
						dojoView={view}
						onSaveDialog={saveDialogStore.onSaveDialog}
					/>
				)}
				customStore={saveDialogStore}
				// default react dialogs conflicting with DOJO popups: react dialogs enforcing focus, maintaining
				// the watch that focus will never leave dialog or its nested elements
				// @material-ui/core/esm/Unstable_TrapFocus/Unstable_TrapFocus.js
				// react dialog being inserted in HTML at body root level
				// dojo popups grow from different "branch" but also from body level, which means that dojo popups, even though they visually
				// inserted inside react dialog, actually are inserted to the root level of HTML body
				// as soon as dojo popup receive focus, react detects this and force focus its own popup dialog,
				// then dojo popup lost focus, trigger onBlur and close
				// disableEnforceFocus - by adding this property we are disabling this react watch and allowing dojo popups to leave
				// (FYI) MUI Trap Focus API: https://mui.com/base/api/trap-focus/
				disableEnforceFocus
				disableSave={disableSave}
				// disableAutoFocus - this property, according to API documentation, responsible for automatic focus of newly created dialog
				// we used to have it enabled, but this caused issue with nested react dialog not being focused, so I
				// commented it out but leave here for a while for memory
				//disableAutoFocus
				fullHeight
				hide={!isFirstViewLoaded}
				// minimum width necessary so that right drawer expand button does not overlap toolbar (assuming standard set of toolbar buttons)
				minWidth={useDrawer ? 725 : null}
				open
				rightDrawerProps={useDrawer ? rightDrawerProps : null}
				scrollContent="none"
				showClose
				zIndex={zIndex}
				{...saveDialogProps}
			>
				{{
					main: hasTabs ? (
						<GoToDialogTabs />
					) : (
						<GoToDialogView {...getViewProps(0)} />
					),
				}}
			</StyledSaveDialog>
		</GoToDialogStoreContext.Provider>
	);
});

export default GoToDialog;
